import { Component, Input, Output, EventEmitter, SimpleChanges, OnInit, OnChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { ComponentBase, Changeable } from "common";
import { IdentityService, LicensingService } from 'identity';
import { DiscResultsService } from 'discovery';
import { MessageService } from 'base';

@Component({
	selector: 'network-settings',
	templateUrl: './network-settings.component.html',
	styleUrls: ['./network-settings.component.scss']
})

export class NetworkSettingsComponent extends ComponentBase implements OnInit, OnChanges, Changeable {

	@Input() public target: NetViewDto;
	public get hasTarget(): boolean {
		return !!this.target;
	}

	public settingsForm: UntypedFormGroup;

	public networkName: UntypedFormControl;
	public networkDesc: UntypedFormControl;
	public eventsInterval: UntypedFormControl;
	public agentAutoUpdate: UntypedFormControl;
	public agentAutoUpdateBeginTime: UntypedFormControl;
	public agentAutoUpdateEndTime: UntypedFormControl;
	public agentInstallerLocation: UntypedFormControl;
	public awolClientAgentsEnabled: UntypedFormControl;
	public awolClientAgentsDays: UntypedFormControl;
	public awolClientAgentsHours: UntypedFormControl;
	public awolClientAgentsMinutes: UntypedFormControl;
	public displayRebuildMasterNetworkConfirm: boolean;

	constructor(private fb: UntypedFormBuilder,
		private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private messageService: MessageService) {
		super();

		this.buildForm();
	}

	ngOnInit() {
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	private buildForm(): void {

		let networkName = "";
		let networkDesc = "";
		let eventsInterval = 0;
		let agentAutoUpdate = true;
		let agentAutoUpdateBeginTime = this.defaultBeginTime;
		let agentAutoUpdateEndTime = this.defaultEndTime;
		let agentInstallerLocation = "";
		let awolClientAgentsEnabled = false;
		let awolClientAgentsDays = 0;
		let awolClientAgentsHours = 0;
		let awolClientAgentsMinutes = 0;

		if (this.hasTarget) {
			networkName = this.target.Name;
			networkDesc = this.target.Description;
			eventsInterval = this.target.EventsInterval;
			agentAutoUpdate = this.target.AgentAutoUpdate;
			agentAutoUpdateBeginTime = this.target.AgentAutoUpdateBeginTime;
			agentAutoUpdateEndTime = this.target.AgentAutoUpdateEndTime;
			agentInstallerLocation = this.target.AgentInstallerLocation;
			awolClientAgentsEnabled = this.target.AwolClientAgents_Enabled;
			awolClientAgentsDays = this.target.AwolClientAgents_Days;
			awolClientAgentsHours = this.target.AwolClientAgents_Hours;
			awolClientAgentsMinutes = this.target.AwolClientAgents_Minutes;
		}

		this.networkName = new UntypedFormControl(networkName, Validators.required);
		this.networkDesc = new UntypedFormControl(networkDesc);
		this.eventsInterval = new UntypedFormControl(eventsInterval,
			Validators.compose([Validators.required, this.minValue(15), this.maxValue(3600)]));
		this.agentAutoUpdate = new UntypedFormControl(agentAutoUpdate);
		this.agentAutoUpdateBeginTime = new UntypedFormControl(agentAutoUpdateBeginTime, Validators.required);
		this.agentAutoUpdateEndTime = new UntypedFormControl(agentAutoUpdateEndTime, Validators.required);
		this.agentInstallerLocation = new UntypedFormControl(agentInstallerLocation);
		this.awolClientAgentsEnabled = new UntypedFormControl(awolClientAgentsEnabled);
		this.awolClientAgentsDays = new UntypedFormControl(awolClientAgentsDays,
			Validators.compose([Validators.required, this.minValue(0), this.maxValue(30)]));
		this.awolClientAgentsHours = new UntypedFormControl(awolClientAgentsHours,
			Validators.compose([Validators.required, this.minValue(0), this.maxValue(23)]));
		this.awolClientAgentsMinutes = new UntypedFormControl(awolClientAgentsMinutes,
			Validators.compose([Validators.required, this.minValue(0), this.maxValue(59)]));

		this.enableDisableControls();

		this.settingsForm = this.fb.group({
			networkName: this.networkName,
			networkDesc: this.networkDesc,
			eventsInterval: this.eventsInterval,
			agentAutoUpdate: this.agentAutoUpdate,
			agentAutoUpdateBeginTime: this.agentAutoUpdateBeginTime,
			agentAutoUpdateEndTime: this.agentAutoUpdateEndTime,
			agentInstallerLocation: this.agentInstallerLocation,
			awolClientAgentsEnabled: this.awolClientAgentsEnabled,
			awolClientAgentsDays: this.awolClientAgentsDays,
			awolClientAgentsHours: this.awolClientAgentsHours,
			awolClientAgentsMinutes: this.awolClientAgentsMinutes,
		},
		{
			validator: this.nonEmptyAwolAgentsSettings()
		});
	}

	public enableDisableControls(): void {
		this.enableDisableFormControl(this.awolClientAgentsDays, this.awolClientAgentsEnabled.value);
		this.enableDisableFormControl(this.awolClientAgentsHours, this.awolClientAgentsEnabled.value);
		this.enableDisableFormControl(this.awolClientAgentsMinutes, this.awolClientAgentsEnabled.value);
	}
	
	private nonEmptyAwolAgentsSettings(): ValidatorFn {
		return () => {
			if (!this.awolClientAgentsEnabled.value) {
				return null;
			}

			if (this.awolClientAgentsDays.value > 0 ||
				this.awolClientAgentsHours.value > 0 ||
				this.awolClientAgentsMinutes.value > 0) {
			
				return null;
			}
		
			return {
				nonEmptyAwolAgentsSettings: { valid: false }
			};
		};
	}

	private get defaultBeginTime(): Date {
		let now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 2, 0, 0, 0);
	}

	private get defaultEndTime(): Date {
		let now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 4, 0, 0, 0);
	}

	public get enableBeginTime(): boolean {
		return this.agentAutoUpdate.value;
	}

	public get enableEndTime(): boolean {
		return this.agentAutoUpdate.value;
	}

	// Changeable

	public get tempChanges(): boolean {
		return this.settingsForm.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.settingsForm.valid;
	}

	public get invalidTempChanges(): string[] {
		return ["Network Settings"];
	}

	public applyTempChanges(): void {

		this.target.Name = this.networkName.value;
		this.target.Description = this.networkDesc.value;
		this.target.EventsInterval = parseInt(this.eventsInterval.value);
		this.target.AgentAutoUpdate = this.agentAutoUpdate.value;
		this.target.AgentAutoUpdateBeginTime = this.agentAutoUpdateBeginTime.value;
		this.target.AgentAutoUpdateEndTime = this.agentAutoUpdateEndTime.value;
		this.target.AgentInstallerLocation = this.agentInstallerLocation.value;
		this.target.AwolClientAgents_Enabled = this.awolClientAgentsEnabled.value;
		this.target.AwolClientAgents_Days = this.awolClientAgentsDays.value;
		this.target.AwolClientAgents_Hours = this.awolClientAgentsHours.value;
		this.target.AwolClientAgents_Minutes = this.awolClientAgentsMinutes.value;

		this.target.HasChanges = true;
		this.buildForm();
	}

	public discardTempChanges(): void {
		this.buildForm();
	}

	public confirmRebuildMasterNetwork(): void {
		this.displayRebuildMasterNetworkConfirm = true;
	}

	public onRebuildMasterOkClicked(): void {
		this.displayRebuildMasterNetworkConfirm = false;

		if (!!this.target && !!this.target.Guid) {
			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.target.Guid;
			this.discResultsService.rebuildMasterNetwork(sessionGuid, netViewGuid).subscribe(
				(result: boolean) => {
					this.messageService.displayInfoMessage("Rebuild master indexes has been initiated");
				},
				(error) => {
					this.messageService.displayErrorMessage("Rebuild failed");
				});
		}
	}

	public onRebuildMasterCancelClicked(): void {
		this.displayRebuildMasterNetworkConfirm = false;
		this.buildForm();
	}
}
